import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconMoon, IconLogout, IconUsers, IconReceipt2, IconStar, IconClock, IconCircleCheck, IconLockOpen } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { useRole } from '../hooks/useRole';
import { useDaySession, type ClosedTodayInfo } from '../hooks/useDaySession';
import { ReopenDayModal } from './DaySessionModals';

/**
 * Phase 4.6 — Post-close lockout screen.
 *
 * Shown when the till loads and a day session was already opened +
 * closed today (SAST). NO money values displayed — staff see this
 * after close, and revenue stays in the emailed Z-Report which only
 * the owner sees.
 *
 * Visual: dark "system-lockout" panel matching DayNotOpenOverlay,
 * but with a celebratory tone — green tick instead of red lock.
 * Slow subtle gradient drift (sunset → night) animates the background.
 */

interface HighlightsResponse {
  session: {
    id: string;
    openedAt: string;
    closedAt: string;
    openedBy: { name: string } | null;
    closedBy: { name: string } | null;
  };
  highlights: {
    orderCount: number;
    customerCount: number;
    busiestHourLabel: string | null;
    topProducts: { name: string; qtySold: number }[];
    topCategories: { name: string; orderCount: number }[];
  };
}

interface Props {
  closedToday: ClosedTodayInfo;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' });
}

function fmtDayDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-ZA', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    timeZone: 'Africa/Johannesburg',
  });
}

export default function DayClosedScreen({ closedToday }: Props) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { hasMinRole } = useRole();
  const { refresh } = useDaySession();
  const isSupervisor = hasMinRole('SUPERVISOR');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [highlights, setHighlights] = useState<HighlightsResponse['highlights'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/day-sessions/closed-today-highlights', { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const data = (await r.json()) as HighlightsResponse;
        setHighlights(data.highlights);
      } catch {
        // Stay graceful — the screen still renders without highlights
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const closedBy = closedToday.closedBy?.name ?? '—';
  const closedAt = fmtTime(closedToday.closedAt);
  const dayLabel = fmtDayDate(closedToday.openedAt);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-6 overflow-hidden"
      style={{
        background:
          'radial-gradient(1200px 800px at 70% 20%, rgba(255,140,80,0.18), transparent 60%),' +
          'radial-gradient(1000px 700px at 20% 80%, rgba(20,100,200,0.22), transparent 60%),' +
          'linear-gradient(160deg, #050a18 0%, #0a0510 50%, #050a18 100%)',
        animation: 'dayClosedDrift 24s ease-in-out infinite alternate',
      }}
    >
      {/* Keyframe injection — single declaration, scoped via global style tag */}
      <style>{`
        @keyframes dayClosedDrift {
          0%   { background-position: 0% 0%, 0% 0%, 0% 0%; filter: hue-rotate(0deg); }
          100% { background-position: 30% 20%, -20% 10%, 0% 0%; filter: hue-rotate(-12deg); }
        }
      `}</style>

      <div className="relative w-full max-w-xl rounded-2xl overflow-hidden animate-slide-up bg-[#0A0A0A]/95 border-2 border-[#1F1F1F] shadow-[0_25px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl">
        {/* Subtle accent stripe — gentle, not alarming */}
        <div className="h-1 bg-gradient-to-r from-[#1B4D2A] via-[#2EBE5A] to-[#1B4D2A]" />

        <div className="p-9 text-center">
          {/* Tick — green halo */}
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-[#2EBE5A]/15 blur-xl" />
            <div className="relative w-20 h-20 rounded-2xl bg-[#021A0B] border-2 border-[#2EBE5A]/40 inline-flex items-center justify-center shadow-lg">
              <IconCircleCheck size={42} stroke={2} className="text-[#3DDB6E]" />
            </div>
          </div>

          {/* Eyebrow */}
          <p className="inline-flex items-center gap-2 text-12 font-bold uppercase tracking-[0.3em] text-[#A8E2BC] mb-3">
            <IconMoon size={14} stroke={2.5} className="text-[#A8E2BC]" />
            Day closed
          </p>

          {/* Day label */}
          <h2 className="text-22 font-bold text-[#FFFFFF] leading-tight mb-1">
            Thanks for today.
          </h2>
          <p className="text-14 font-medium text-[#A0A4AC] mb-1.5">{dayLabel}</p>
          <p className="text-12 text-[#7A7E84] mb-7">
            Closed by <span className="font-semibold text-[#B8BCC2]">{closedBy}</span> at <span className="tabular-nums">{closedAt}</span>
          </p>

          {/* Non-monetary highlights tiles */}
          {highlights && (
            <div className="grid grid-cols-2 gap-3 mb-6 text-left">
              <Tile Icon={IconReceipt2} label="Orders" value={highlights.orderCount.toString()} />
              <Tile Icon={IconUsers} label="Customers" value={highlights.customerCount > 0 ? String(highlights.customerCount) : 'Walk-ins only'} />
              {highlights.busiestHourLabel && (
                <Tile Icon={IconClock} label="Busiest hour" value={highlights.busiestHourLabel} />
              )}
              {highlights.topProducts[0] && (
                <Tile
                  Icon={IconStar}
                  label="Top product"
                  value={highlights.topProducts[0].name}
                  sub={`${highlights.topProducts[0].qtySold} sold`}
                />
              )}
            </div>
          )}

          {/* Top categories chip row */}
          {highlights && highlights.topCategories.length > 0 && (
            <div className="mb-7">
              <p className="text-11 font-bold uppercase tracking-widest text-[#7A7E84] mb-2">Top categories</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {highlights.topCategories.map((c) => (
                  <span key={c.name} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#15151A] border border-[#2A2A30] text-12 font-bold text-[#D8DAE0]">
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sign out — only action */}
          <button
            type="button"
            onClick={async () => { await signOut(); navigate('/login'); }}
            className="w-full h-13 inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#15151A] border-2 border-[#2A2A30] text-[#FFFFFF] text-15 font-bold hover:bg-[#1A1A22] hover:border-[#3A3A44] transition-all active:scale-[0.99]"
            style={{ height: '52px' }}
          >
            <IconLogout size={18} stroke={2.25} />
            Sign out · See you tomorrow
          </button>

          {/* Reopen for late sale — supervisor only. Phase 4.15. */}
          {isSupervisor && (
            <button
              type="button"
              onClick={() => setReopenOpen(true)}
              className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-xl bg-transparent border border-[#2A2A30] text-[#A0A4AC] text-12 font-semibold hover:text-[#FFFFFF] hover:border-[#3A3A44] transition-all mt-2"
            >
              <IconLockOpen size={14} stroke={2.25} />
              Reopen for a late sale
            </button>
          )}

          <p className="text-11 text-[#5A5E64] mt-5">
            The till is locked for the rest of today. It will open again from midnight (SAST).
          </p>
        </div>

        <div className="h-1 bg-gradient-to-r from-[#1B4D2A] via-[#2EBE5A] to-[#1B4D2A]" />
      </div>

      <ReopenDayModal
        open={reopenOpen}
        sessionId={closedToday.id}
        onClose={() => setReopenOpen(false)}
        onReopened={async () => { setReopenOpen(false); await refresh(); }}
      />
    </div>
  );
}

function Tile({ Icon, label, value, sub }: { Icon: typeof IconReceipt2; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-[#15151A]/80 border border-[#2A2A30] p-3 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-[#7A7E84]">
        <Icon size={14} stroke={2.25} />
        <p className="text-11 font-bold uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-15 font-bold text-[#FFFFFF] mt-1 truncate">{value}</p>
      {sub && <p className="text-11 font-medium text-[#7A7E84] mt-0.5">{sub}</p>}
    </div>
  );
}
